import { isKey } from 'default/ts/common/utils';

export interface Ad {
    id: string;
    slot: string;
    dimensions: googletag.GeneralSize;
    targeting?: { [key: string]: string | string[] };
}

export interface WindowWithAds extends Window {
    stpd: {
        que: any[],
        initialize: any,
        initializeAdUnit?: (id: string) => void,
        adUnits?: { code: string }[],
    };
    actualAds: Ad[];
    adsInitCompleted?: boolean;
    contentBucket?: string;
}

export type WindowWithAdsT = WindowWithAds & typeof globalThis;

export const windowWithAds = (window as WindowWithAdsT);

/*
 * Lazy-loaded ad slots (Setupad H/B lazy-loading: a slot is defined, displayed and
 * handed to stpd.initializeAdUnit only once the reader gets near it, so its auction,
 * creative and ActiveView scripts stay out of the page load).
 */
// Distance (in % of viewport height) from the viewport at which a lazy slot starts loading.
const LAZY_AD_MARGIN_PERCENT = 150;

// .a-wrapper (a-wrapper.scss) and some ad containers stay display:none until GPT renders
// an iframe into them, so the slot itself has no box: it measures top 0 and never
// intersects. Position is taken from the nearest ancestor that is laid out instead.
const layoutAnchor = (element: Element): Element => {
    let node: Element | null = element;

    while (node && node.getClientRects().length === 0) {
        node = node.parentElement;
    }

    return node ?? element;
};

export const isNearViewport = (element: Element): boolean => layoutAnchor(element)
    .getBoundingClientRect().top < window.innerHeight * (1 + LAZY_AD_MARGIN_PERCENT / 100);

const whenAdsReady = (callback: () => void): void => {
    if (windowWithAds.adsInitCompleted) {
        callback();
    } else {
        document.addEventListener('gpt_init', callback, { once: true });
    }
};

export function displayAd(ad: Ad): void {
    const display = () => {
        if (googletag.pubads().isInitialLoadDisabled()) {
            googletag
                .cmd.push(() => { googletag.display(ad.id); });
        }
    };

    if (windowWithAds.adsInitCompleted) {
        display();
    } else {
        document.addEventListener('gpt_init', () => display());
    }
}

export function initAd(
    ad: Ad,
): void {
    const init = () => {
        const slot = googletag
            .defineSlot(ad.slot, ad.dimensions, ad.id);

        slot?.addService(googletag.pubads());

        const targeting = ad.targeting || null;

        if (targeting && slot) {
            Object.keys(targeting).forEach((targetingKey) => {
                if (isKey(targeting, targetingKey)) {
                    slot.setTargeting(targetingKey, targeting[targetingKey]);
                }
            });
        }
    };

    if (
        googletag.apiReady
    ) {
        init();
    } else {
        document.addEventListener('gpt_init', () => init());
    }
}

// Setupad only auctions units configured on its side (e.g. not the 970x250 in-article
// units): initializeAdUnit registers a known unit into stpd.adUnits synchronously and
// silently ignores the rest. Those are requested from GPT directly instead.
const requestAdUnit = (id: string): void => {
    windowWithAds.stpd.initializeAdUnit?.(id);

    if (windowWithAds.stpd.adUnits?.some((unit) => unit.code === id)) {
        return;
    }

    const slot = googletag.pubads().getSlots().find((s) => s.getSlotElementId() === id);

    if (slot) {
        googletag.pubads().refresh([slot]);
    }
};

const initializeSetupadUnit = (id: string): void => {
    windowWithAds.stpd = windowWithAds.stpd || { que: [] };

    if (typeof windowWithAds.stpd.initializeAdUnit === 'function') {
        requestAdUnit(id);

        return;
    }

    // Setupad's script hasn't loaded yet; queue the call for when it has.
    windowWithAds.stpd.que.push(() => requestAdUnit(id));
};

const loadLazyAd = (ad: Ad): void => {
    whenAdsReady(() => {
        windowWithAds.googletag?.cmd.push(() => {
            if (!document.getElementById(ad.id)) {
                return;
            }

            initAd(ad);
            googletag.display(ad.id);
            initializeSetupadUnit(ad.id);
        });
    });
};

// Keyed by layout anchor; several slots can share one when their own containers are hidden.
const pendingLazyAds = new Map<Element, Ad[]>();
let lazyAdObserver: IntersectionObserver | null = null;

/**
 * Defers a slot until the reader scrolls near it (measured on its nearest laid-out
 * ancestor, see layoutAnchor), then defines and displays it and runs Setupad's auction
 * for that slot alone.
 */
export function lazyLoadAd(ad: Ad): void {
    const element = document.getElementById(ad.id);

    if (!element) {
        return;
    }

    if (!lazyAdObserver) {
        lazyAdObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                const pending = pendingLazyAds.get(entry.target);

                if (!entry.isIntersecting || !pending) {
                    return;
                }

                lazyAdObserver?.unobserve(entry.target);
                pendingLazyAds.delete(entry.target);
                pending.forEach(loadLazyAd);
            });
        }, { rootMargin: `${LAZY_AD_MARGIN_PERCENT}% 0px` });
    }

    const anchor = layoutAnchor(element);
    const pending = pendingLazyAds.get(anchor);

    if (pending) {
        pending.push(ad);

        return;
    }

    pendingLazyAds.set(anchor, [ad]);
    lazyAdObserver.observe(anchor);
}

/**
 * Registers ad slots added to the DOM after the initial GPT setup (infinite-scroll
 * appended articles, the sidebar ad stack). Each slot loads lazily through Setupad
 * once the reader gets near it. Each ad.id must be unique and already present in
 * the DOM.
 */
export function registerAndDisplayAds(ads: Ad[]): void {
    ads.forEach(lazyLoadAd);
}
